
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Filter dropdown
 *
 * @file TableFilter.jsx
 * @author Anuja Gaikwad
 * @since 1.4.7
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Typography,
  Button,
  Popover,
  MenuItem
} from '@material-ui/core';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

import TickIcon from '../icons/TickIcon';

const TableFilter = ({
  classes,
  filterByText,
  filterConfig,
  value,
  onChange
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const openDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeDropdown = () => {
    setAnchorEl(null);
  };

  const selectedOption =
    filterConfig?.options?.find(
      (option) => option.value === value
    ) || filterConfig?.options?.[0];

  return (
    <>
      <div className={classes.filterConatiner}>
        <Typography className={classes.filterLabel}>
          {filterByText}
        </Typography>

        <Button
          className={classes.filterSelect}
          onClick={openDropdown}
          disableRipple
          disableFocusRipple
          disableElevation
        >
          <div className={classes.filterSelectContent}>
            <Typography className={classes.filterSelectedText}>
              {selectedOption?.label}
            </Typography>

            {anchorEl ? (
              <ArrowDropUpIcon className={classes.filterArrowIcon} />
            ) : (
              <ArrowDropDownIcon className={classes.filterArrowIcon} />
            )}
          </div>
        </Button>
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        classes={{
          paper: classes.filterPopover
        }}
      >
        <div className={classes.filterDropdownMenu}>
          {filterConfig.options.map((option) => (
            <MenuItem
              key={option.id}
              selected={value === option.value}
              className={classes.filterMenuItem}
              onClick={() => {
                onChange(option.value);
                closeDropdown();
              }}
            >
              <div className={classes.filterMenuItemContent}>
                <Typography
                  className={
                    value === option.value
                      ? classes.filterMenuItemSelectedText
                      : classes.filterMenuItemText
                  }
                >
                  {option.label}
                </Typography>

                {value === option.value && (
                  <TickIcon className={classes.filterCheckIcon} />
                )}
              </div>
            </MenuItem>
          ))}
        </div>
      </Popover>
    </>
  );
};

TableFilter.propTypes = {
  classes: PropTypes.object.isRequired,
  filterByText: PropTypes.string.isRequired,
  filterConfig: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export default TableFilter;
